import { supabase } from "@/shared/lib/supabaseClient";
import type { Database } from "@/shared/types/database.types";

export type PushSubscriptionRow = Database["public"]["Tables"]["push_subscriptions"]["Row"];

interface NovaAssinaturaPush {
  endpoint: string;
  p256dh: string;
  auth: string;
  userAgent: string | null;
  pessoaId: string;
}

export type ResultadoRegistroPush = "criada" | "reativada" | "conflito";

/**
 * Camada de dados de `push_subscriptions` (Frente 2 — arquitetura de
 * notificações, migration 0069). Implementa o desenho de lifecycle aprovado
 * em 03/09/2026 (não reabrir):
 *
 *   1. logout -> `ativo=false` (nunca DELETE) — ver AuthContext.logout.
 *   2. Retorno da MESMA pessoa a um dispositivo com subscription existente
 *      (talvez desativada) -> pode reativar, mas só através do fluxo normal
 *      de permissão/consentimento (usePushSubscription.ativar — nunca
 *      automático).
 *   3. Endpoint já pertencente a OUTRA pessoa (dispositivo compartilhado)
 *      -> NUNCA reatribui o dono silenciosamente. Por isso este service usa
 *      INSERT simples (nunca upsert): um upsert por `endpoint` reatribuiria
 *      a linha à pessoa logada mesmo que pertencesse a outra. Ao colidir com
 *      a constraint unique de `endpoint`, busca a linha existente — a RLS
 *      (`pessoa_id = pessoa_id_atual()`) só devolve a linha se ela já for da
 *      pessoa logada; se vier vazia, o endpoint é de outra pessoa e o
 *      resultado "conflito" é devolvido para o chamador tratar (descartar a
 *      subscription do navegador e assinar de novo, gerando um endpoint
 *      genuinamente diferente — nunca tocar na linha da outra pessoa).
 *   4. Histórico sempre preservado via `ativo=false`, nunca DELETE.
 */
export const pushService = {
  async registrarOuReativar(dados: NovaAssinaturaPush): Promise<ResultadoRegistroPush> {
    const { error: erroInsert } = await supabase.from("push_subscriptions").insert({
      pessoa_id: dados.pessoaId,
      endpoint: dados.endpoint,
      p256dh: dados.p256dh,
      auth: dados.auth,
      user_agent: dados.userAgent,
    });

    if (!erroInsert) return "criada";

    // 23505 = unique_violation (Postgres). Qualquer outro erro é real e deve
    // subir para a UI — não tentar interpretar como conflito de endpoint.
    if (erroInsert.code !== "23505") {
      throw new Error("Não foi possível registrar as notificações push.");
    }

    const { data: existente, error: erroSelect } = await supabase
      .from("push_subscriptions")
      .select("id")
      .eq("endpoint", dados.endpoint)
      .maybeSingle();

    if (erroSelect) {
      throw new Error("Não foi possível verificar a assinatura existente.");
    }

    if (!existente) {
      // A linha existe (colidiu na constraint), mas a RLS não a devolveu:
      // pertence a outra pessoa. Conflito de dispositivo compartilhado.
      return "conflito";
    }

    const { error: erroUpdate } = await supabase
      .from("push_subscriptions")
      .update({
        ativo: true,
        p256dh: dados.p256dh,
        auth: dados.auth,
        user_agent: dados.userAgent,
        last_seen_at: new Date().toISOString(),
      })
      .eq("id", existente.id);

    if (erroUpdate) {
      throw new Error("Não foi possível reativar a assinatura de notificações.");
    }

    return "reativada";
  },

  /**
   * Desliga a subscription deste dispositivo (logout, ou "Desativar" na
   * tela de preferências). Nunca apaga a linha — preserva histórico
   * (item 4 do desenho de lifecycle).
   */
  async desativarPorEndpoint(endpoint: string): Promise<void> {
    const { error } = await supabase
      .from("push_subscriptions")
      .update({ ativo: false })
      .eq("endpoint", endpoint);
    if (error) throw new Error("Não foi possível desativar a assinatura de notificações.");
  },

  /**
   * Consulta (somente leitura) se a linha correspondente a este endpoint
   * está `ativo=true` no banco — usado só para decidir o que exibir na UI
   * (PN-17, item B). Nunca escreve nada; nunca reativa nada sozinho. Sujeito
   * à mesma RLS de SELECT (`pessoa_id = pessoa_id_atual()`): se a linha não
   * existir, ou pertencer a outra pessoa, retorna `false` — estado seguro,
   * nunca superestima como "ativo".
   */
  async estaAtivoNoBanco(endpoint: string): Promise<boolean> {
    const { data, error } = await supabase
      .from("push_subscriptions")
      .select("ativo")
      .eq("endpoint", endpoint)
      .maybeSingle();

    if (error || !data) return false;
    return data.ativo === true;
  },
};
